import { X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAsterSettings } from "./useAsterSettings";
import { defaultAsterSettings, type AsterContext, type AsterSettings } from "./types";

type AsterGuideProps = {
  context: AsterContext;
  heading?: string;
  scene?: string;
  message?: string;
  detail?: string;
  memberName?: string | null;
  appearanceKey?: string;
  important?: boolean;
  onceEver?: boolean;
  inline?: boolean;
  permanent?: boolean;
  settingsOverride?: Partial<AsterSettings>;
  actions?: Array<{ label: string; to?: string; onClick?: () => void }>;
};

const contextLabels: Record<AsterContext, string> = {
  homepage: "Foundation guidance",
  onboarding: "Meet your Foundation Star Guide",
  atlas: "Aster’s Observatory",
  course: "Star University guidance",
  reflection: "A moment for reflection",
  sky: "Reading your Foundation Sky",
  program: "Current Foundation opportunity",
  ceremony: "A moment of recognition",
  celebration: "A milestone in your journey",
};

const contextHeadings: Record<AsterContext, string> = {
  homepage: "A steady light for the journey ahead",
  onboarding: "Welcome to the Foundation Sky",
  atlas: "Your next step is ready when you are",
  course: "Let’s connect this lesson to real life",
  reflection: "Pause, notice, and carry the learning forward",
  sky: "Every constellation holds a larger story",
  program: "Here is one way to take part",
  ceremony: "This contribution belongs in the record",
  celebration: "Your journey has reached a new milestone",
};

function sessionKey(context: AsterContext, appearanceKey?: string) {
  return `guiding-stars:aster:${context}:${appearanceKey ?? "default"}`;
}

export function AsterGuide({
  context,
  heading,
  message,
  detail,
  memberName,
  appearanceKey,
  important = false,
  onceEver = false,
  inline = false,
  permanent = false,
  settingsOverride,
  actions,
}: AsterGuideProps) {
  const query = useAsterSettings();
  const settings = useMemo(
    () => ({ ...defaultAsterSettings, ...query.data, ...settingsOverride }) as AsterSettings,
    [query.data, settingsOverride],
  );
  const [visible, setVisible] = useState(permanent);
  const key = sessionKey(context, appearanceKey);

  useEffect(() => {
    if (!settings.enabled) return;
    if (permanent) return;
    if (settings.appearance_frequency === "important_only" && !important) return;
    if (onceEver && window.localStorage.getItem(key)) return;
    if (
      settings.appearance_frequency === "once_per_session" &&
      window.sessionStorage.getItem(key)
    ) {
      return;
    }
    const entrance = window.setTimeout(() => setVisible(true), inline ? 120 : 900);
    if (settings.appearance_frequency === "once_per_session") {
      window.sessionStorage.setItem(key, "seen");
    }
    if (onceEver) window.localStorage.setItem(key, "celebrated");
    return () => window.clearTimeout(entrance);
  }, [
    important,
    inline,
    key,
    onceEver,
    permanent,
    settings.appearance_frequency,
    settings.enabled,
  ]);

  useEffect(() => {
    if (!visible || inline || important) return;
    const timeout = window.setTimeout(
      () => setVisible(false),
      Math.max(8, Math.min(settings.display_seconds, 30)) * 1000,
    );
    return () => window.clearTimeout(timeout);
  }, [important, inline, settings.display_seconds, visible]);

  if (!visible) return null;

  const title =
    heading ??
    (context === "onboarding" && memberName
      ? `Welcome to the Foundation Sky, ${memberName.split(" ")[0]}.`
      : contextHeadings[context]);
  const mainMessage =
    message ??
    (context === "homepage"
      ? settings.homepage_message
      : context === "course"
        ? settings.course_introduction
        : context === "reflection"
          ? settings.reflection_prompt
          : settings.current_initiative_message || settings.mission_text);

  return (
    <aside
      className={`aster-guide aster-${context}${inline ? " is-inline" : ""}${permanent ? " is-permanent" : ""} motion-${settings.animation_frequency}`}
      aria-label={contextLabels[context]}
      aria-live={important ? "assertive" : "polite"}
    >
      <div className="aster-dialogue">
        <span>{contextLabels[context]}</span>
        <strong>{title}</strong>
        <p>{mainMessage}</p>
        {settings.seasonal_message && context === "homepage" && (
          <small>{settings.seasonal_message}</small>
        )}
        {detail && <small>{detail}</small>}
        {actions?.length ? (
          <nav className="aster-actions" aria-label="Aster’s suggested next steps">
            {actions.map((action) =>
              action.to ? (
                <Link key={`${action.label}-${action.to}`} to={action.to}>
                  {action.label}
                </Link>
              ) : (
                <button key={action.label} type="button" onClick={action.onClick}>
                  {action.label}
                </button>
              ),
            )}
          </nav>
        ) : null}
      </div>
      {!permanent && (
        <button type="button" onClick={() => setVisible(false)} aria-label="Close Aster’s message">
          <X size={17} />
        </button>
      )}
    </aside>
  );
}
